import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Brain, Heart, Target, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, ReferenceLine, CartesianGrid, Legend } from 'recharts';
import { API } from '../../App';

const trendIcon = { closing: TrendingDown, widening: TrendingUp, steady: Minus };
const trendCopy = {
  closing: 'Closing — Awareness is becoming Action',
  widening: 'Widening — knowing without doing',
  steady: 'Steady — the same gap remains',
};

const TheGapPage = () => {
  const [gap, setGap] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await API.get('/locker/gap');
        setGap(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF9]">
        <div className="w-8 h-8 border-2 border-[#2C5530] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const series = (gap?.series || []).map((s, i) => ({
    i: i + 1,
    label: new Date(s.round_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
    gap_aa: s.gap_aa,
    awareness: s.awareness,
    acceptance: s.acceptance,
    action: s.action,
  }));

  const TrendIcon = gap?.trend_aa ? trendIcon[gap.trend_aa] : Minus;

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="the-gap-page">
      <div className="bg-[#1A2E1D] text-white">
        <div className="container mx-auto px-6 md:px-10 py-4 max-w-5xl flex items-center justify-between">
          <Link to="/locker" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm transition-colors" data-testid="back-to-locker">
            <ArrowLeft className="w-4 h-4" /> Locker
          </Link>
        </div>
      </div>

      <main className="container mx-auto px-6 md:px-10 py-10 max-w-5xl">
        <div className="mb-8">
          <p className="text-[#BC562B] text-xs uppercase tracking-[0.25em] mb-2 flex items-center gap-2">
            <Target className="w-4 h-4" /> The Gap
          </p>
          <h1 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#1A1A1A] mb-2">Awareness vs. Action</h1>
          <p className="text-[#57534E] max-w-2xl leading-relaxed">
            "The Gap" is the distance between what you saw and what you did. The closer it gets to zero, the more your mind and game are aligned.
          </p>
        </div>

        {/* KPI cards */}
        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-white border border-[#E7E5E4] p-5" data-testid="gap-overall-aa">
            <p className="text-xs uppercase tracking-[0.2em] text-[#737373] mb-1">Overall gap (A1 − A3)</p>
            <p className="font-['Playfair_Display'] text-4xl text-[#1A2E1D] tabular-nums">
              {gap?.overall_gap_aa == null ? '—' : (gap.overall_gap_aa > 0 ? `+${gap.overall_gap_aa}` : gap.overall_gap_aa)}
            </p>
            <p className="text-xs text-[#57534E] mt-1">Across all sessions you've logged.</p>
          </div>
          <div className="bg-white border border-[#E7E5E4] p-5" data-testid="gap-latest-aa">
            <p className="text-xs uppercase tracking-[0.2em] text-[#737373] mb-1">Latest round gap</p>
            <p className="font-['Playfair_Display'] text-4xl text-[#1A2E1D] tabular-nums">
              {gap?.latest_gap_aa == null ? '—' : (gap.latest_gap_aa > 0 ? `+${gap.latest_gap_aa}` : gap.latest_gap_aa)}
            </p>
            <p className="text-xs text-[#57534E] mt-1">From your most recent round review.</p>
          </div>
          <div className="bg-white border border-[#E7E5E4] p-5" data-testid="gap-trend">
            <p className="text-xs uppercase tracking-[0.2em] text-[#737373] mb-1 flex items-center gap-1">
              <TrendIcon className="w-3.5 h-3.5" /> Trend
            </p>
            <p className="font-['Playfair_Display'] text-2xl text-[#1A2E1D]">
              {gap?.trend_aa ? gap.trend_aa.charAt(0).toUpperCase() + gap.trend_aa.slice(1) : '—'}
            </p>
            <p className="text-xs text-[#57534E] mt-1">{gap?.trend_aa ? trendCopy[gap.trend_aa] : 'Need 4+ reviews to compute a trend.'}</p>
          </div>
        </div>

        {/* Averages */}
        <div className="bg-white border border-[#E7E5E4] p-6 mb-8" data-testid="gap-averages">
          <h2 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-4">Your KPI averages</h2>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div>
              <Brain className="w-5 h-5 mx-auto text-[#2C5530] mb-1" />
              <p className="text-[10px] uppercase tracking-wider text-[#737373]">Awareness</p>
              <p className="font-['Playfair_Display'] text-3xl text-[#2C5530] tabular-nums">
                {gap?.averages?.awareness ?? '—'}<span className="text-sm text-[#737373]">/10</span>
              </p>
            </div>
            <div>
              <Heart className="w-5 h-5 mx-auto text-[#BC562B] mb-1" />
              <p className="text-[10px] uppercase tracking-wider text-[#737373]">Acceptance</p>
              <p className="font-['Playfair_Display'] text-3xl text-[#BC562B] tabular-nums">
                {gap?.averages?.acceptance ?? '—'}<span className="text-sm text-[#737373]">/10</span>
              </p>
            </div>
            <div>
              <ArrowRight className="w-5 h-5 mx-auto text-[#1A2E1D] mb-1" />
              <p className="text-[10px] uppercase tracking-wider text-[#737373]">Action</p>
              <p className="font-['Playfair_Display'] text-3xl text-[#1A2E1D] tabular-nums">
                {gap?.averages?.action ?? '—'}<span className="text-sm text-[#737373]">/10</span>
              </p>
            </div>
          </div>
        </div>

        {/* Chart: Gap over time */}
        <div className="bg-white border border-[#E7E5E4] p-6 mb-6" data-testid="gap-chart">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-['Playfair_Display'] text-xl text-[#1A1A1A]">Gap over time</h2>
            <p className="text-xs text-[#737373]">{gap?.review_count || 0} round review{gap?.review_count === 1 ? '' : 's'}</p>
          </div>
          {series.length === 0 ? (
            <div className="text-center py-12">
              <Target className="w-10 h-10 text-[#E7E5E4] mx-auto mb-3" />
              <p className="text-[#737373] mb-3">No round reviews yet — log one to see your gap chart.</p>
              <Link to="/locker/round/new" className="inline-flex items-center gap-1 text-[#2C5530] text-sm font-semibold hover:text-[#1E3A21]">
                Start a round review <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ) : (
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={series} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F2F0E9" />
                  <XAxis dataKey="label" stroke="#A8A29E" tick={{ fontSize: 11 }} />
                  <YAxis domain={[-10, 10]} stroke="#A8A29E" tick={{ fontSize: 11 }} />
                  <ReferenceLine y={0} stroke="#BC562B" strokeDasharray="4 4" label={{ value: 'Aligned', fill: '#BC562B', fontSize: 10, position: 'right' }} />
                  <Tooltip
                    contentStyle={{ background: '#1A2E1D', border: 'none', color: 'white', borderRadius: 4 }}
                    formatter={(v, name) => [v, name === 'gap_aa' ? 'Gap' : name]}
                  />
                  <Line type="monotone" dataKey="gap_aa" stroke="#1A2E1D" strokeWidth={2.5} dot={{ r: 4, fill: '#BC562B' }} activeDot={{ r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Compare KPIs overlay chart */}
        <div className="bg-white border border-[#E7E5E4] p-6" data-testid="compare-kpis-chart">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#737373] mb-1">Compare KPIs</p>
              <h2 className="font-['Playfair_Display'] text-xl text-[#1A1A1A]">Awareness · Acceptance · Action</h2>
            </div>
            <p className="text-xs text-[#737373] hidden sm:block">Side-by-side, per round review</p>
          </div>
          {series.length === 0 ? (
            <div className="text-center py-12">
              <Brain className="w-10 h-10 text-[#E7E5E4] mx-auto mb-3" />
              <p className="text-[#737373] mb-3">No round reviews yet — log one to compare all 3 KPIs over time.</p>
              <Link to="/locker/round/new" className="inline-flex items-center gap-1 text-[#2C5530] text-sm font-semibold hover:text-[#1E3A21]">
                Start a round review <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ) : (
            <div className="h-[320px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={series} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F2F0E9" />
                  <XAxis dataKey="label" stroke="#A8A29E" tick={{ fontSize: 11 }} />
                  <YAxis domain={[0, 10]} stroke="#A8A29E" tick={{ fontSize: 11 }} />
                  <Tooltip
                    contentStyle={{ background: '#1A2E1D', border: 'none', color: 'white', borderRadius: 4 }}
                    formatter={(v, name) => [`${v}/10`, name.charAt(0).toUpperCase() + name.slice(1)]}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
                    iconType="circle"
                  />
                  <Line type="monotone" dataKey="awareness" name="Awareness" stroke="#2C5530" strokeWidth={2.5} dot={{ r: 3, fill: '#2C5530' }} activeDot={{ r: 5 }} connectNulls />
                  <Line type="monotone" dataKey="acceptance" name="Acceptance" stroke="#BC562B" strokeWidth={2.5} dot={{ r: 3, fill: '#BC562B' }} activeDot={{ r: 5 }} connectNulls />
                  <Line type="monotone" dataKey="action" name="Action" stroke="#1A2E1D" strokeWidth={2.5} dot={{ r: 3, fill: '#1A2E1D' }} activeDot={{ r: 5 }} connectNulls strokeDasharray="0" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
          <p className="text-xs text-[#737373] mt-3">
            When all three lines move together, your mind and game are aligned. When Awareness pulls ahead of Action, that's where The Gap appears.
          </p>
        </div>
      </main>
    </div>
  );
};

export default TheGapPage;
